// Shared workflow-stage derivation — the SINGLE source of truth for how far a
// project has progressed through the linear workflow
//   Upload → Intake → Analysis → Assessment → Review → Matching → Disclosure
//
// Both the WorkflowProgressBar strip AND the header "potential matches" badge
// derive their state from this one function, so the two can never tell
// contradictory stories (e.g. the header asserting "5 potential matches" while
// the bar still shows Matching as an unreached, greyed-out future stage).
//
// Each stage's completion is driven by a real backend artifact, not a
// frontend-only guess:
//   Upload     ← documents exist for the project
//   Intake     ← invention_declaration set on the project record
//   Analysis   ← project analysis summary exists (real pipeline output)
//   Assessment ← three-axis assessment scores exist
//   Review     ← review_status === 'approved' on the project record
//   Matching   ← cross-pollination consent granted (discoverable) AND a match run
//                has actually completed with ≥1 match
//   Disclosure ← disclosure workflow completed

export type ProgressStageStatus = 'completed' | 'active' | 'pending'

export interface ProgressStage {
  name: string
  status: ProgressStageStatus
}

export const WORKFLOW_STAGE_NAMES = [
  'Upload',
  'Intake',
  'Analysis',
  'Assessment',
  'Review',
  'Matching',
  'Disclosure',
] as const

export interface WorkflowStageInputs {
  hasDeclaration?: boolean
  hasDocuments?: boolean
  hasAnalysis?: boolean
  hasScores?: boolean
  hasReviewApproved?: boolean
  hasMatching?: boolean
  matchCount?: number
  runStatus?: string
  disclosureComplete?: boolean
}

export function computeWorkflowStages({
  hasDeclaration = false,
  hasDocuments = false,
  hasAnalysis = false,
  hasScores = false,
  hasReviewApproved = false,
  hasMatching = false,
  matchCount = 0,
  runStatus = '',
  disclosureComplete = false,
}: WorkflowStageInputs): ProgressStage[] {
  const isRunning = runStatus === 'in_progress' || runStatus === 'pending'
  const matchingDone = hasMatching && matchCount >= 1 && !isRunning

  const completions = [
    hasDocuments,
    hasDeclaration,
    hasAnalysis,
    hasScores,
    hasReviewApproved,
    matchingDone,
    disclosureComplete,
  ]

  const firstIncomplete = completions.findIndex((c) => !c)

  // BUG-D / P3-10: monotonic display. The workflow is sequential, so once we hit
  // the first incomplete stage, everything after it is 'pending' — even if a later
  // stage's artifact happens to exist. Out-of-order backend data (e.g. Review
  // approved before Assessment scores are written, or matches computed while the
  // project still sits at Intake) must never render a later step 'completed' or
  // 'active' while an earlier one is not.
  return WORKFLOW_STAGE_NAMES.map((name, i) => {
    if (firstIncomplete === -1) return { name, status: 'completed' as const }
    if (i < firstIncomplete) return { name, status: 'completed' as const }
    if (i === firstIncomplete) return { name, status: 'active' as const }
    return { name, status: 'pending' as const }
  })
}

// True once the workflow has advanced to (or past) the named stage — i.e. the
// stage is 'active' or 'completed', not a greyed-out future 'pending'. Used to
// gate the header overlap badge so it defers to workflow position.
export function hasReachedStage(stages: ProgressStage[], name: string): boolean {
  const stage = stages.find((s) => s.name === name)
  return !!stage && stage.status !== 'pending'
}
